import React, { useState, useEffect } from "react";
import "@/App.css";
import { 
  Scissors, 
  MapPin, 
  Phone, 
  Star, 
  Instagram, 
  Clock, 
  ChevronDown,
  ChevronUp,
  Menu,
  X,
  Users,
  Sparkles,
  Award,
  CheckCircle,
  Navigation,
  Shield,
  Heart,
  Smile,
  Calendar,
  BadgeCheck,
  ArrowRight,
  ExternalLink,
  Baby,
  Facebook,
  Home
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Separator } from "./components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";

// Constants
const BOOKSY_URL = "https://booksy.com/en-us/1138395_max-mood-barbershop_barber-shop_19311_naperville#ba_s=seo";
const HIRING_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSfbS_7v2-BdyvuaAWt-Td1AlzAftscGcuz_bjhYIXvRJUqLMg/viewform?pli=1";
const INSTAGRAM_HANDLE = "max.mood.barbershop";
const PHONE_NUMBER = "(929) 900-0355";
const PHONE_LINK = "tel:9299000355";
const ADDRESS = "931 W 75th St Suite 111, Naperville, IL 60540";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_maxmood-barber/artifacts/cwtpemel_photo_2025-11-27_13-48-14.jpg";

// Images
const HERO_BG = "https://customer-assets.emergentagent.com/job_maxmood-barber/artifacts/ac2v1hq4_download%20%281%29.png";
const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_maxmood-barber/artifacts/65gi15dg_9d142ff663e14834baae242640e80d97.jpg";

// Social Media Links
const FACEBOOK_URL = "https://www.facebook.com/share/1AwA37V4wa/?mibextid=wwXIfr";
const NEXTDOOR_URL = "https://nextdoor.com/!L5A6KB";
const HIRING_BG = "https://images.unsplash.com/photo-1625038032200-648fbcd800d0?crop=entropy&cs=srgb&fm=jpg&q=85";

// Gallery media - videos and images from the barbershop
const galleryMedia = [
  { type: "video", src: "https://customer-assets.emergentagent.com/job_maxmood-barber/artifacts/bwtoljgk_IMG_9628.MP4" },
  { type: "video", src: "https://customer-assets.emergentagent.com/job_maxmood-barber/artifacts/ghov5zvs_IMG_9627.MP4" },
  { type: "image", src: "https://customer-assets.emergentagent.com/job_maxmood-barber/artifacts/adm06s1e_18aee623-64a0-4dd0-bf7f-272b0298758a.png" },
  { type: "video", src: "https://customer-assets.emergentagent.com/job_maxmood-barber/artifacts/h3frw154_IMG_8789.MOV" },
];

// Services with real pricing
const services = [
  { name: "Haircut", description: "Precision cut tailored to your style", duration: "30 min", price: "$32", icon: Scissors },
  { name: "Haircut & Beard", description: "Complete grooming package", duration: "45 min", price: "$47", icon: Award },
  { name: "Beard Trim & Shape", description: "Clean lines and perfect shape", duration: "15 min", price: "$19", icon: Sparkles },
  { name: "Kids Haircut", description: "Patient, friendly service for young gents", duration: "20 min", price: "$26", icon: Baby },
  { name: "Beard Coloring", description: "Natural-looking color enhancement", duration: "30 min", price: "$20", icon: Sparkles },
  { name: "Black Mask", description: "Deep cleansing facial treatment", duration: "15 min", price: "$10", icon: Shield },
  { name: "Facial", description: "Relaxing skin care treatment", duration: "30 min", price: "$40", icon: Heart },
  { name: "Beard Trim Extended", description: "Detailed beard sculpting service", duration: "25 min", price: "$25", icon: CheckCircle },
  { name: "Haircut & Beard Premium", description: "Premium full-service grooming", duration: "50 min", price: "$60", icon: BadgeCheck },
];

// Testimonials
const testimonials = [
  { name: "Tommy Gallagher", text: "Best hair cut in town and the price is unbeatable for the service.", rating: 5, verified: true },
  { name: "Bekzod Rakhmonov", text: "Best barbershop in the area, prices also very good.", rating: 5, verified: true },
  { name: "Daniil Bertsel", text: "Excellent place, excellent service and quality of services.", rating: 5, verified: true },
  { name: "Marcus Johnson", text: "My go-to spot for a fresh fade. The barbers here really know their craft!", rating: 5, verified: true },
  { name: "David Chen", text: "Clean shop, skilled barbers, and great vibes. Highly recommend!", rating: 5, verified: true },
  { name: "James Wilson", text: "Finally found a barbershop in Naperville that gets fades right. Worth every penny.", rating: 5, verified: true },
];

// Why Choose Us reasons
const whyChooseUs = [
  { title: "Skilled Barbers", description: "Our experienced team delivers precision cuts with attention to every detail.", icon: Award },
  { title: "Clean Modern Shop", description: "A welcoming, professional environment you'll enjoy visiting.", icon: Sparkles },
  { title: "Great for All Ages", description: "From kids to adults, everyone gets the same quality service.", icon: Users },
  { title: "Easy Booking", description: "Book online in seconds or walk in anytime. We make it simple.", icon: Calendar },
];

// FAQ data
const faqs = [
  { question: "Do you accept walk-ins?", answer: "Yes! Walk-ins are always welcome at Max Mood Barbershop. We serve customers on a first-come, first-served basis, but booking online ensures you get your preferred time slot." },
  { question: "Do you cut kids' hair?", answer: "Absolutely! We love working with our younger clients. Our barbers are patient and experienced with kids of all ages, making the experience fun and comfortable." },
  { question: "Do you offer beard trims?", answer: "Yes, we offer a full range of beard services including beard trims, shaping, and beard coloring. Our barbers are skilled in creating clean lines and perfect shapes." },
  { question: "Where are you located?", answer: "We're located at 931 W 75th St Suite 111, Naperville, IL 60540, in the Hobson West Plaza. Easy parking available right in front of the shop." },
  { question: "How do I book an appointment?", answer: "You can book online through Booksy by clicking any 'Book Appointment' button on our website, or call us at (929) 900-0355. Walk-ins are also welcome!" },
];

// Navigation component
const NavBar = ({ isScrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  const navLinks = [
    { name: "Services", href: "#services" },
    { name: "Gallery", href: "#gallery" },
    { name: "Reviews", href: "#reviews" },
    { name: "About", href: "#about" },
    { name: "Contact", href: "#location" },
  ];

  return (
    <nav 
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-obsidian/95 backdrop-blur-md border-b border-white/5 shadow-lg' : 'bg-obsidian/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center h-20">
          
          {/* Left Side - Logo & Brand Name */}
          <a href="#hero" className="flex items-center gap-4 group flex-shrink-0" data-testid="nav-logo">
            <img 
              src={LOGO_URL} 
              alt="Max Mood Barbershop Naperville" 
              className="h-14 w-14 object-contain rounded-full transition-transform group-hover:scale-105 border-2 border-gold-500/30"
            />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-oswald text-xl font-bold uppercase tracking-wide text-white group-hover:text-gold-500 transition-colors">
                Max Mood
              </span>
              <span className="font-oswald text-xl font-bold uppercase tracking-wide text-white group-hover:text-gold-500 transition-colors mt-0.5">
                Barbershop
              </span>
            </div>
          </a>

          {/* Spacer - pushes nav to right */}
          <div className="flex-1 min-w-[60px]" />

          {/* Right Side - Navigation Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="nav-link text-sm font-semibold uppercase tracking-widest text-neutral-300 hover:text-gold-500 transition-colors whitespace-nowrap"
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            
            {/* Separator */}
            <div className="h-8 w-px bg-white/20 mx-2" />

            {/* CTA Buttons */}
            <a href={PHONE_LINK} data-testid="nav-phone-btn" className="group flex-shrink-0">
              <Button variant="ghost" className="text-white hover:text-gold-500 hover:bg-gold-500/10 transition-all px-3">
                <Phone className="w-4 h-4 mr-2" />
                <span className="font-semibold text-sm whitespace-nowrap">{PHONE_NUMBER}</span>
              </Button>
            </a>
            <a href={BOOKSY_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-book-btn" className="flex-shrink-0">
              <Button className="bg-gold-500 text-black hover:bg-white font-oswald uppercase tracking-wider font-bold px-5 py-2 shadow-lg hover:shadow-gold-500/30 transition-all whitespace-nowrap">
                Book Now
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2 hover:bg-white/5 rounded-md transition-colors ml-4"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden mobile-menu fixed top-20 left-0 right-0 border-t border-white/10 bg-obsidian backdrop-blur-lg z-50" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block text-lg font-oswald uppercase tracking-wider text-neutral-300 hover:text-gold-500 py-3 border-b border-white/5 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <div className="pt-4 space-y-3">
              <a 
                href={BOOKSY_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block"
                data-testid="mobile-book-btn"
              >
                <Button className="w-full bg-gold-500 text-black hover:bg-white font-oswald uppercase tracking-wider font-bold py-4">
                  Book Appointment
                </Button>
              </a>
              <a href={PHONE_LINK} className="block" data-testid="mobile-phone-btn">
                <Button variant="outline" className="w-full border-gold-500/50 text-gold-500 hover:bg-gold-500/10 font-oswald uppercase py-4">
                  <Phone className="w-4 h-4 mr-2" />
                  {PHONE_NUMBER}
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center hero-bg"
      style={{ backgroundImage: `url(${HERO_BG})` }}
    >
      <div className="hero-overlay absolute inset-0" />
      
      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto pt-20">
        {/* Rating Badge */}
        <div className="inline-flex items-center gap-2 bg-gold-500/15 border border-gold-500/40 rounded-full px-5 py-2.5 mb-8 animate-fade-in">
          <div className="flex gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 text-gold-500 fill-gold-500" />
            ))}
          </div>
          <span className="text-sm font-semibold text-gold-400">4.8 Stars • 539+ Google Reviews</span>
        </div>

        {/* Main Headline */}
        <h1 className="font-oswald text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight text-white mb-6 text-shadow animate-fade-in animate-delay-100 leading-tight">
          Naperville's Trusted Barbershop for{' '}
          <span className="text-gold-500">Precision Cuts, Fades & Beard Trims</span>
        </h1>

        {/* Subheadline */}
        <p className="font-manrope text-lg md:text-xl text-neutral-300 max-w-3xl mx-auto mb-10 animate-fade-in animate-delay-200 leading-relaxed">
          Sharp cuts, clean fades, beard trims, and kids cuts from skilled barbers in Naperville. 
          Walk-ins welcome or book online in minutes.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in animate-delay-300">
          <a href={BOOKSY_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-book-btn">
            <Button className="btn-gold text-lg px-10 py-6 gold-glow group">
              Book Appointment
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
          <a href={PHONE_LINK} data-testid="hero-call-btn">
            <Button className="btn-outline text-lg px-10 py-6 group">
              <Phone className="w-5 h-5 mr-2 group-hover:animate-pulse" />
              Call Now
            </Button>
          </a>
        </div>

        {/* Trust Row */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mt-12 animate-fade-in animate-delay-400">
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
            <Star className="w-4 h-4 text-gold-500 fill-gold-500" />
            <span className="text-sm text-neutral-300 font-medium">4.8★ Google Reviews</span>
          </div>
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
            <CheckCircle className="w-4 h-4 text-gold-500" />
            <span className="text-sm text-neutral-300 font-medium">Walk-ins Welcome</span>
          </div>
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
            <Clock className="w-4 h-4 text-gold-500" />
            <span className="text-sm text-neutral-300 font-medium">Open Daily Until 8 PM</span>
          </div>
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
            <MapPin className="w-4 h-4 text-gold-500" />
            <span className="text-sm text-neutral-300 font-medium">Hobson West Plaza</span>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-gold-500" />
        </div>
      </div>
    </section>
  );
};

// Local Trust Section
const LocalTrustSection = () => {
  return (
    <section className="bg-charcoal py-8 border-y border-white/5" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="flex items-center gap-3 justify-center md:justify-start">
            <div className="p-2.5 bg-gold-500/10 rounded-lg">
              <MapPin className="w-5 h-5 text-gold-500" />
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wider">Address</p>
              <p className="text-sm text-white font-medium">931 W 75th St #111</p>
            </div>
          </div>
          <div className="flex items-center gap-3 justify-center md:justify-start">
            <div className="p-2.5 bg-gold-500/10 rounded-lg">
              <Phone className="w-5 h-5 text-gold-500" />
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wider">Phone</p>
              <a href={PHONE_LINK} className="text-sm text-gold-400 font-medium hover:text-gold-500">{PHONE_NUMBER}</a>
            </div>
          </div>
          <div className="flex items-center gap-3 justify-center md:justify-start">
            <div className="p-2.5 bg-gold-500/10 rounded-lg">
              <Clock className="w-5 h-5 text-gold-500" />
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wider">Hours</p>
              <p className="text-sm text-white font-medium">Daily until 8 PM</p>
            </div>
          </div>
          <div className="flex items-center gap-3 justify-center md:justify-start">
            <div className="p-2.5 bg-gold-500/10 rounded-lg">
              <Users className="w-5 h-5 text-gold-500" />
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wider">Family Friendly</p>
              <p className="text-sm text-white font-medium">Kids Welcome</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section with real pricing
const ServicesSection = () => {
  return (
    <section id="services" className="py-20 md:py-28 bg-obsidian" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="text-sm font-medium tracking-widest uppercase text-gold-500 mb-4 block">
            What We Offer
          </span>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-4">
            Services & Pricing
          </h2>
          <p className="font-manrope text-neutral-400 max-w-2xl mx-auto">
            Professional cuts, fades, and grooming services. Prices may vary slightly depending on the barber.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, index) => (
            <Card 
              key={service.name}
              className="service-card bg-charcoal border-white/5 hover:border-gold-500/50 group overflow-hidden"
              data-testid={`service-card-${index}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-gold-500/10 rounded-lg group-hover:bg-gold-500/20 transition-colors">
                    <service.icon className="w-5 h-5 text-gold-500" />
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-oswald font-bold text-gold-400">{service.price}</span>
                    <p className="text-xs text-neutral-500">{service.duration}</p>
                  </div>
                </div>
                <h3 className="font-oswald text-lg font-semibold uppercase tracking-wider text-white mb-2">
                  {service.name}
                </h3>
                <p className="text-neutral-400 text-sm leading-relaxed">
                  {service.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pricing Note */}
        <p className="text-center text-sm text-neutral-500 mt-8 italic">
          Prices shown are standard rates. Final price may vary slightly depending on the barber's experience and service details.
        </p>

        {/* CTA */}
        <div className="text-center mt-10">
          <a href={BOOKSY_URL} target="_blank" rel="noopener noreferrer" data-testid="services-book-btn">
            <Button className="bg-gold-500 text-black hover:bg-white font-oswald uppercase tracking-wider font-bold px-10 py-4 shadow-lg hover:shadow-gold-500/30 transition-all group">
              Book Your Appointment
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const [selectedMedia, setSelectedMedia] = useState(null);

  return (
    <section id="gallery" className="py-20 md:py-28 bg-charcoal" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="text-sm font-medium tracking-widest uppercase text-gold-500 mb-4 block">
            Our Work
          </span>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-4">
            See Our Work
          </h2>
          <p className="font-manrope text-neutral-400 max-w-2xl mx-auto">
            From fades to beard work, every cut is done with attention to detail.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {galleryMedia.map((media, index) => (
            <div 
              key={index}
              className="relative aspect-video overflow-hidden rounded-lg cursor-pointer group bg-obsidian"
              onClick={() => setSelectedMedia(media)}
              data-testid={`gallery-item-${index}`}
            >
              {media.type === "video" ? (
                <video 
                  src={media.src}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  muted
                  loop
                  playsInline
                  onMouseEnter={(e) => e.target.play()}
                  onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                />
              ) : (
                <img 
                  src={media.src} 
                  alt={`Barbershop work ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                <span className="text-white font-oswald uppercase tracking-wider text-sm flex items-center gap-2">
                  {media.type === "video" ? (
                    <>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                      </svg>
                      Play Video
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4" />
                      View Image
                    </>
                  )}
                </span>
              </div>
              {media.type === "video" && (
                <div className="absolute top-3 right-3 bg-gold-500 text-black px-2 py-1 rounded text-xs font-bold uppercase">
                  Video
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Social Media Links */}
        <div className="text-center mt-10">
          <p className="text-neutral-400 mb-4">Follow us on social media</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href={`https://www.instagram.com/${INSTAGRAM_HANDLE}/`} 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="gallery-instagram-btn"
            >
              <Button variant="outline" className="border-gold-500/50 text-gold-500 hover:bg-gold-500/10 font-oswald uppercase tracking-wider">
                <Instagram className="w-4 h-4 mr-2" />
                Instagram
              </Button>
            </a>
            <a 
              href={FACEBOOK_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="gallery-facebook-btn"
            >
              <Button variant="outline" className="border-gold-500/50 text-gold-500 hover:bg-gold-500/10 font-oswald uppercase tracking-wider">
                <Facebook className="w-4 h-4 mr-2" />
                Facebook
              </Button>
            </a>
            <a 
              href={NEXTDOOR_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="gallery-nextdoor-btn"
            >
              <Button variant="outline" className="border-gold-500/50 text-gold-500 hover:bg-gold-500/10 font-oswald uppercase tracking-wider">
                <Home className="w-4 h-4 mr-2" />
                Nextdoor
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Lightbox for media */}
      {selectedMedia && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full z-10"
            onClick={() => setSelectedMedia(null)}
          >
            <X className="w-8 h-8" />
          </button>
          {selectedMedia.type === "video" ? (
            <video 
              src={selectedMedia.src}
              className="max-w-full max-h-[90vh] rounded-lg"
              controls
              autoPlay
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img 
              src={selectedMedia.src} 
              alt="Gallery preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          )}
        </div>
      )}
    </section>
  );
};

// Reviews Section
const ReviewsSection = () => {
  return (
    <section id="reviews" className="py-20 md:py-28 bg-obsidian" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="text-sm font-medium tracking-widest uppercase text-gold-500 mb-4 block">
            What Clients Say
          </span>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6">
            Customer Reviews
          </h2>
          
          {/* Rating Display */}
          <div className="inline-flex items-center gap-4 bg-charcoal px-8 py-4 rounded-lg border border-gold-500/30">
            <div className="text-center">
              <div className="font-oswald text-5xl font-bold text-gold-500">4.8</div>
              <div className="flex gap-1 mt-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-gold-500 fill-gold-500" />
                ))}
              </div>
            </div>
            <Separator orientation="vertical" className="h-16 bg-white/10" />
            <div className="text-left">
              <div className="font-oswald text-2xl font-bold text-white">539+</div>
              <div className="text-sm text-neutral-400">Google Reviews</div>
            </div>
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-charcoal p-6 rounded-lg border-l-4 border-gold-500 hover:bg-charcoal/80 transition-colors"
              data-testid={`testimonial-${index}`}
            >
              <div className="flex items-center gap-2 mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-gold-500 fill-gold-500" />
                ))}
                {testimonial.verified && (
                  <BadgeCheck className="w-4 h-4 text-green-500 ml-auto" />
                )}
              </div>
              <p className="text-neutral-300 italic mb-4 leading-relaxed">"{testimonial.text}"</p>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gold-500/20 rounded-full flex items-center justify-center">
                  <span className="text-gold-500 font-bold text-sm">{testimonial.name.charAt(0)}</span>
                </div>
                <span className="font-oswald uppercase tracking-wider text-white font-semibold text-sm">
                  {testimonial.name}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <a 
            href="https://www.google.com/search?q=max+mood+barbershop+naperville+reviews" 
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="google-reviews-btn"
          >
            <Button variant="outline" className="border-white/20 text-white hover:border-gold-500 hover:text-gold-500 font-oswald uppercase tracking-wider">
              See More Reviews
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  return (
    <section className="py-20 md:py-28 bg-charcoal" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="text-sm font-medium tracking-widest uppercase text-gold-500 mb-4 block">
            The Max Mood Difference
          </span>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-4">
            Why Choose Us
          </h2>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {whyChooseUs.map((item, index) => (
            <div 
              key={index}
              className="text-center p-6 bg-obsidian rounded-lg border border-white/5 hover:border-gold-500/30 transition-colors group"
              data-testid={`why-choose-${index}`}
            >
              <div className="inline-flex p-4 bg-gold-500/10 rounded-full mb-4 group-hover:bg-gold-500/20 transition-colors">
                <item.icon className="w-8 h-8 text-gold-500" />
              </div>
              <h3 className="font-oswald text-xl font-semibold uppercase tracking-wider text-white mb-2">
                {item.title}
              </h3>
              <p className="text-neutral-400 text-sm leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-20 md:py-28 bg-obsidian" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative order-2 lg:order-1">
            <div className="aspect-[4/5] overflow-hidden rounded-lg">
              <img 
                src={ABOUT_IMG} 
                alt="Max Mood Barbershop Interior" 
                className="w-full h-full object-cover"
              />
            </div>
            {/* Accent box */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-gold-500 rounded-lg hidden lg:block" />
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <span className="text-sm font-medium tracking-widest uppercase text-gold-500 mb-4 block">
              Our Story
            </span>
            <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6">
              Naperville's Premier{' '}
              <span className="text-gold-500">Barbershop</span>
            </h2>
            <div className="space-y-4 text-neutral-300 font-manrope leading-relaxed">
              <p>
                At Max Mood Barbershop, we focus on clean work, consistent quality, and a welcoming experience. 
                Whether you need a sharp fade, a beard cleanup, or a kids cut, our team delivers detail-driven 
                service that keeps clients coming back.
              </p>
              <p>
                Located in Hobson West Plaza, we've built a reputation as Naperville's go-to spot for 
                precision haircuts. Our skilled barbers bring years of experience and genuine passion 
                for the craft to every client.
              </p>
              <p>
                Walk-ins are always welcome, or book online for your preferred time. We're proud to 
                serve the Naperville community with professional grooming services in a modern, 
                comfortable environment.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-10 pt-10 border-t border-white/10">
              <div>
                <div className="font-oswald text-3xl md:text-4xl font-bold text-gold-500">4.8</div>
                <div className="text-sm text-neutral-400 uppercase tracking-wider mt-1">Star Rating</div>
              </div>
              <div>
                <div className="font-oswald text-3xl md:text-4xl font-bold text-gold-500">539+</div>
                <div className="text-sm text-neutral-400 uppercase tracking-wider mt-1">Reviews</div>
              </div>
              <div>
                <div className="font-oswald text-3xl md:text-4xl font-bold text-gold-500">7</div>
                <div className="text-sm text-neutral-400 uppercase tracking-wider mt-1">Days Open</div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8">
              <a href={BOOKSY_URL} target="_blank" rel="noopener noreferrer" data-testid="about-book-btn">
                <Button className="bg-gold-500 text-black hover:bg-white font-oswald uppercase tracking-wider font-bold">
                  Book Your Visit
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Location Section
const LocationSection = () => {
  return (
    <section id="location" className="py-20 md:py-28 bg-charcoal" data-testid="location-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Info */}
          <div>
            <span className="text-sm font-medium tracking-widest uppercase text-gold-500 mb-4 block">
              Find Us
            </span>
            <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-8">
              Visit Our Shop
            </h2>
            
            <div className="space-y-6">
              {/* Address */}
              <div className="flex items-start gap-4 p-4 bg-obsidian rounded-lg">
                <div className="p-3 bg-gold-500/10 rounded-lg">
                  <MapPin className="w-6 h-6 text-gold-500" />
                </div>
                <div>
                  <h3 className="font-oswald uppercase tracking-wider text-white font-semibold mb-1">Address</h3>
                  <p className="text-neutral-300">{ADDRESS}</p>
                  <p className="text-gold-400 text-sm mt-1">Located in Hobson West Plaza</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4 p-4 bg-obsidian rounded-lg">
                <div className="p-3 bg-gold-500/10 rounded-lg">
                  <Phone className="w-6 h-6 text-gold-500" />
                </div>
                <div>
                  <h3 className="font-oswald uppercase tracking-wider text-white font-semibold mb-1">Phone</h3>
                  <a href={PHONE_LINK} className="text-gold-400 hover:text-gold-500 transition-colors text-lg font-semibold">
                    {PHONE_NUMBER}
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start gap-4 p-4 bg-obsidian rounded-lg">
                <div className="p-3 bg-gold-500/10 rounded-lg">
                  <Clock className="w-6 h-6 text-gold-500" />
                </div>
                <div>
                  <h3 className="font-oswald uppercase tracking-wider text-white font-semibold mb-1">Hours</h3>
                  <p className="text-neutral-300">Open Daily</p>
                  <p className="text-neutral-400 text-sm">Closes at 8 PM</p>
                </div>
              </div>

              {/* Highlights */}
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-full text-sm text-gold-400 font-medium">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  Walk-ins Welcome
                </span>
                <span className="px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-full text-sm text-gold-400 font-medium">
                  <Smile className="w-4 h-4 inline mr-1" />
                  Good for Kids
                </span>
                <span className="px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-full text-sm text-gold-400 font-medium">
                  <Shield className="w-4 h-4 inline mr-1" />
                  Restroom Available
                </span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <a 
                href="https://www.google.com/maps/dir/?api=1&destination=931+W+75th+St+Suite+111+Naperville+IL+60540" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="get-directions-btn"
              >
                <Button className="bg-gold-500 text-black hover:bg-white font-oswald uppercase tracking-wider font-bold w-full sm:w-auto">
                  <Navigation className="w-4 h-4 mr-2" />
                  Get Directions
                </Button>
              </a>
              <a href={PHONE_LINK} data-testid="location-call-btn">
                <Button variant="outline" className="border-gold-500/50 text-gold-500 hover:bg-gold-500/10 font-oswald uppercase tracking-wider w-full sm:w-auto">
                  <Phone className="w-4 h-4 mr-2" />
                  Call to Book
                </Button>
              </a>
            </div>
          </div>

          {/* Map */}
          <div className="h-[400px] lg:h-[500px] rounded-lg overflow-hidden border border-white/10">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2978.5089774563177!2d-88.14877492340873!3d41.74834827125957!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880e57c50c1dca5b%3A0x5a4f0d3c9b9b9c9c!2s931%20W%2075th%20St%20Suite%20111%2C%20Naperville%2C%20IL%2060540!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'grayscale(80%) contrast(1.1)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Max Mood Barbershop Location"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  return (
    <section id="faq" className="py-20 md:py-28 bg-obsidian" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="text-sm font-medium tracking-widest uppercase text-gold-500 mb-4 block">
            Questions?
          </span>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-4">
            Frequently Asked
          </h2>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-charcoal border border-white/10 rounded-lg px-6 data-[state=open]:border-gold-500/50"
              data-testid={`faq-item-${index}`}
            >
              <AccordionTrigger className="text-left font-oswald uppercase tracking-wider text-white hover:text-gold-500 py-5">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-neutral-400 pb-5 leading-relaxed">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Additional CTA */}
        <div className="text-center mt-10">
          <p className="text-neutral-400 mb-4">Still have questions?</p>
          <a href={PHONE_LINK} data-testid="faq-call-btn">
            <Button variant="outline" className="border-gold-500/50 text-gold-500 hover:bg-gold-500/10 font-oswald uppercase tracking-wider">
              <Phone className="w-4 h-4 mr-2" />
              Call Us: {PHONE_NUMBER}
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Hiring Section
const HiringSection = () => {
  return (
    <section 
      id="hiring" 
      className="relative py-20 md:py-28"
      data-testid="hiring-section"
    >
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HIRING_BG})` }}
      />
      <div className="absolute inset-0 bg-obsidian/90" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="text-sm font-medium tracking-widest uppercase text-gold-500 mb-4 block">
          Join Our Team
        </span>
        <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6">
          We're Hiring{' '}
          <span className="text-gold-500">Professional Barbers</span>
        </h2>
        <p className="font-manrope text-lg text-neutral-300 max-w-2xl mx-auto mb-10 leading-relaxed">
          Join a high-traffic barbershop with great clients and a strong team. 
          We offer competitive pay, flexible hours, and real growth opportunity 
          in a busy Naperville location. If you're passionate about your craft, 
          we want to hear from you.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href={HIRING_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="apply-now-btn">
            <Button className="bg-gold-500 text-black hover:bg-white font-oswald uppercase tracking-wider font-bold px-10 py-4 gold-glow group">
              Apply Now
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
          <a href={PHONE_LINK} data-testid="hiring-call-btn">
            <Button variant="outline" className="border-white/20 text-white hover:border-gold-500 hover:text-gold-500 font-oswald uppercase tracking-wider px-10 py-4">
              <Phone className="w-4 h-4 mr-2" />
              Call for Info
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-black border-t border-white/5 pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Logo & About */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src={LOGO_URL} 
                alt="Max Mood Barbershop Naperville" 
                className="h-14 w-14 object-contain rounded-full"
              />
              <span className="font-oswald text-xl font-bold uppercase tracking-wider text-white">
                Max Mood Barbershop
              </span>
            </div>
            <p className="text-neutral-400 mb-4 max-w-md leading-relaxed">
              Naperville's premier destination for precision haircuts, fades, beard trims, and professional grooming. 
              Walk-ins welcome or book online.
            </p>
            <p className="text-sm text-neutral-500 italic">
              Serving Naperville, Illinois with premium barber services.
            </p>
            <div className="flex gap-4 mt-6">
              <a 
                href={`https://www.instagram.com/${INSTAGRAM_HANDLE}/`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-3 bg-white/5 hover:bg-gold-500/20 rounded-lg transition-colors group"
                data-testid="footer-instagram"
                title="Follow us on Instagram"
              >
                <Instagram className="w-5 h-5 text-neutral-400 group-hover:text-gold-500 transition-colors" />
              </a>
              <a 
                href={FACEBOOK_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-3 bg-white/5 hover:bg-gold-500/20 rounded-lg transition-colors group"
                data-testid="footer-facebook"
                title="Follow us on Facebook"
              >
                <Facebook className="w-5 h-5 text-neutral-400 group-hover:text-gold-500 transition-colors" />
              </a>
              <a 
                href={NEXTDOOR_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-3 bg-white/5 hover:bg-gold-500/20 rounded-lg transition-colors group"
                data-testid="footer-nextdoor"
                title="Find us on Nextdoor"
              >
                <Home className="w-5 h-5 text-neutral-400 group-hover:text-gold-500 transition-colors" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-oswald uppercase tracking-wider text-white font-semibold mb-5">Quick Links</h3>
            <ul className="space-y-3">
              <li><a href="#services" className="text-neutral-400 hover:text-gold-500 transition-colors">Services & Pricing</a></li>
              <li><a href="#gallery" className="text-neutral-400 hover:text-gold-500 transition-colors">Gallery</a></li>
              <li><a href="#reviews" className="text-neutral-400 hover:text-gold-500 transition-colors">Reviews</a></li>
              <li><a href="#about" className="text-neutral-400 hover:text-gold-500 transition-colors">About Us</a></li>
              <li><a href="#location" className="text-neutral-400 hover:text-gold-500 transition-colors">Location</a></li>
              <li><a href="#hiring" className="text-neutral-400 hover:text-gold-500 transition-colors">Careers</a></li>
              <li>
                <a href={BOOKSY_URL} target="_blank" rel="noopener noreferrer" className="text-gold-400 hover:text-gold-500 transition-colors font-medium">
                  Book Online →
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-oswald uppercase tracking-wider text-white font-semibold mb-5">Contact</h3>
            <ul className="space-y-4 text-neutral-400">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="block">{ADDRESS}</span>
                  <span className="text-sm text-gold-400">Hobson West Plaza</span>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gold-500 flex-shrink-0" />
                <a href={PHONE_LINK} className="hover:text-gold-500 transition-colors font-medium">
                  {PHONE_NUMBER}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gold-500 flex-shrink-0" />
                <span>Open Daily until 8 PM</span>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="bg-white/10 mb-8" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-neutral-500 text-sm">
            &copy; {currentYear} Max Mood Barbershop Naperville. All rights reserved.
          </p>
          <a href={BOOKSY_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-book-btn">
            <Button className="bg-gold-500 text-black hover:bg-white font-oswald uppercase tracking-wider font-bold">
              Book Appointment
            </Button>
          </a>
        </div>
      </div>
    </footer>
  );
};

// Floating Book Button
const FloatingBookButton = ({ show }) => {
  if (!show) return null;
  
  return (
    <div className="fixed bottom-6 right-6 z-40" data-testid="floating-book-btn">
      <a href={BOOKSY_URL} target="_blank" rel="noopener noreferrer">
        <Button className="bg-gold-500 text-black hover:bg-white font-oswald uppercase tracking-wider font-bold shadow-2xl gold-glow px-6 py-3 group">
          <Scissors className="w-4 h-4 mr-2 group-hover:rotate-45 transition-transform" />
          Book Now
        </Button>
      </a>
    </div>
  );
};

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowFloatingButton(window.scrollY > 600);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="App bg-obsidian min-h-screen" data-testid="app-container">
      <NavBar 
        isScrolled={isScrolled} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      
      <main>
        <HeroSection />
        <LocalTrustSection />
        <ServicesSection />
        <GallerySection />
        <ReviewsSection />
        <WhyChooseUsSection />
        <AboutSection />
        <LocationSection />
        <FAQSection />
        <HiringSection />
      </main>
      
      <Footer />
      <FloatingBookButton show={showFloatingButton} />
    </div>
  );
}

export default App;
